import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Plus, 
  Lightbulb, 
  MoreVertical, 
  Trash2, 
  FolderOpen,
  LogOut,
  Clock,
  ArrowRight
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error(t('common_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newTitle.trim()) {
      toast.error(t('common_error'));
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: newTitle }),
      });

      if (response.ok) {
        const project = await response.json();
        setShowNewDialog(false);
        setNewTitle("");
        navigate(`/project/${project.project_id}`);
      } else {
        toast.error(t('common_error'));
      }
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(t('common_error'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setProjects(projects.filter((p) => p.project_id !== projectId));
        toast.success(t('common_success'));
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(t('common_error'));
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/");
    }
  };

  const getStepLabel = (step) => {
    const steps = [
      t('wizard_problem'),
      t('wizard_ideas'),
      t('wizard_value_prop'),
      t('wizard_business_model'),
      t('wizard_concept'),
      t('wizard_prototype'),
      t('wizard_pitch'),
    ];
    return steps[step - 1] || t('wizard_problem');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-semibold text-lg text-stone-900">
                {t('brand_name')}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary text-white">
                        {user?.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-stone-700">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900">
              {t('dashboard_title')}
            </h1>
            <p className="text-stone-600 mt-1">
              {t('dashboard_subtitle')}
            </p>
          </div>
          <Button
            onClick={() => setShowNewDialog(true)}
            className="btn-primary"
            data-testid="new-project-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('dashboard_new_project')}
          </Button>
        </div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                <div className="h-6 bg-stone-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-stone-100 rounded w-full mb-2"></div>
                <div className="h-4 bg-stone-100 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-10 h-10 text-stone-400" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              {t('dashboard_no_projects')}
            </h2>
            <p className="text-stone-600 mb-6 max-w-md mx-auto">
              {t('dashboard_no_projects_desc')}
            </p>
            <Button
              onClick={() => setShowNewDialog(true)}
              className="btn-primary"
              data-testid="empty-new-project-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('dashboard_create_first')}
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.project_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className="bg-white border-stone-200 hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/project/${project.project_id}`)}
                  data-testid={`project-card-${project.project_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-heading text-lg text-stone-900 group-hover:text-primary transition-colors line-clamp-1">
                        {project.title}
                      </CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4 text-stone-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.project_id);
                            }}
                            className="text-red-600 cursor-pointer"
                            data-testid={`delete-project-${project.project_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('dashboard_delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <CardDescription className="line-clamp-2">
                      {project.problem_statement || t('dashboard_no_projects')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        <Clock className="w-4 h-4" />
                        {formatDate(project.updated_at)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-primary-light text-primary px-2 py-1 rounded-full font-medium">
                          {t('dashboard_step')} {project.current_step}/7
                        </span>
                        <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-stone-500">
                      {getStepLabel(project.current_step)}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* New Project Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('dialog_create_title')}</DialogTitle>
            <DialogDescription>
              {t('dialog_create_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder={t('dialog_create_placeholder')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
              className="focus-ring"
              data-testid="new-project-title-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewDialog(false)}
              className="btn-secondary"
            >
              {t('dialog_cancel')}
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={isCreating}
              className="btn-primary"
              data-testid="create-project-submit-btn"
            >
              {isCreating ? t('common_creating') : t('dialog_create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
